import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, Ticket, Trophy } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { api, errorMessage } from "../../lib/api";
import { basarOrderUrl, compressNumbers, nok } from "../../lib/basar";
import { normalizePhone } from "./BuyPanel";
import { useLang } from "../../context/LanguageContext";

/** «Mine lodd»: kjøparen slår opp betalte lodd (og eventuelle gevinstar) med mobilnummeret sitt. Viser berre nummer og ordrereferanse. */
export const MyTicketsDialog = ({ basarId, trigger, drawn = false }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const lookup = async (e) => {
    e.preventDefault();
    const digits = normalizePhone(phone);
    if (!digits) {
      setError(t("Fyll inn et gyldig mobilnummer (8 siffer)"));
      return;
    }
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(`/basar/${basarId}/lookup`, { params: { phone: digits } });
      setResult(data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setResult(null); setError(""); } }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" data-testid="basar-my-tickets-button"><Ticket className="w-4 h-4 mr-1.5" /> {t("Mine lodd")}</Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="basar-my-tickets-dialog">
        <DialogHeader>
          <DialogTitle>{t("Mine lodd")}</DialogTitle>
          <DialogDescription>{t("Skriv inn mobilnummeret du brukte ved kjøpet, så finner vi loddene dine.")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={lookup} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="basar-lookup-phone">{t("Mobilnummer")}</Label>
            <div className="flex gap-2">
              <Input id="basar-lookup-phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="912 34 567" autoFocus data-testid="basar-lookup-phone-input" />
              <Button type="submit" disabled={loading} data-testid="basar-lookup-submit-button">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}<span className="sr-only">{t("Søk")}</span></Button>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="basar-lookup-error">{error}</p>}
          </div>
        </form>
        {result && (
          <div className="space-y-3" data-testid="basar-lookup-result">
            {result.numbers?.length ? (
              <>
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">{result.numbers.length === 1 ? t("{n} lodd registrert på nummeret", { n: 1 }) : t("{n} lodd registrert på nummeret", { n: result.numbers.length })}</p>
                  <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto" data-testid="basar-lookup-numbers">
                    {result.numbers.map((n) => <span key={n} className="inline-flex items-center rounded-md border border-border bg-muted/50 px-2 py-1 text-sm font-semibold tabular-nums">{n}</span>)}
                  </div>
                </div>
                {result.won?.length > 0 && (
                  <div className="rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-3 space-y-1" data-testid="basar-lookup-won">
                    <p className="text-sm font-semibold inline-flex items-center gap-1.5"><Trophy className="w-4 h-4 text-[hsl(var(--warning))]" /> {t("Gratulerer – du har vunnet!")}</p>
                    {result.won.map((w, i) => <p key={i} className="text-sm tabular-nums">{t("Lodd {n}", { n: w.number })}: {w.prize_name}{w.value ? ` (${nok(w.value)})` : ""}</p>)}
                  </div>
                )}
                {drawn && !result.won?.length && <p className="text-sm text-muted-foreground" data-testid="basar-lookup-no-win">{t("Ingen av loddene dine ble trukket denne gangen. Takk for støtten!")}</p>}
                <ul className="text-xs text-muted-foreground space-y-1">
                  {result.orders.map((o) => (
                    <li key={o.id}><Link to={basarOrderUrl(o.id)} className="underline underline-offset-4 hover:text-foreground transition-colors duration-150" data-testid={`basar-lookup-order-${o.id}`}>{t("Ordre {number}", { number: o.order_number })}</Link> · {compressNumbers(o.numbers)}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="basar-lookup-empty">{t("Vi fant ingen betalte lodd på dette nummeret i denne basaren.")}</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
